import React, { useState, useMemo, useEffect } from 'react';
import {
  Wrapper,
  EllipsisSpan,
  useUpdate,
  useBreadcrumbRoutes,
  Button,
  DatePicker,
  Form,
  Input,
  Select,
  CustomFilter,
  Paging,
  usePaging,
  Table,
  message,
} from '@maxtropy/components';
import { Space, Upload as AntdUpload } from 'antd';
import type { UploadChangeParam } from 'antd/es/upload';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs, { Dayjs } from 'dayjs';
import { useRequest } from 'ahooks';
import {
  apiV2BillingMeterReportPagePost,
  apiV2BillingMeterReportDepartmentsPost,
  apiV2BillingMeterConfigListPost,
  V2BillingMeterReportPagePostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { keepTwoOrLine } from '@/utils/utils';
import styles from './index.module.scss';

// 数据类型定义
export type BillingMeterReportItem = Exclude<V2BillingMeterReportPagePostResponse['list'], undefined>[number];

// 表格行数据类型（转换后的扁平结构）
export interface TableRowData {
  key: string;
  collectorCode?: string;
  department?: string;
  deviceName?: string;
  [key: string]: any; // 动态日期列数据
}

// 筛选参数类型
export interface FilterParams {
  department?: string; // 部门
  collectorCode?: string; // 采集器编号
  deviceName?: string; // 设备名称
  dateRange?: [Dayjs, Dayjs]; // 统计时间
}

// 搜索参数类型（转换为接口参数格式）
export interface SearchParams {
  department?: string;
  collectorCode?: string;
  deviceName?: string;
  startTime?: number;
  endTime?: number;
}

const dateFormat = 'YYYY-MM-DD';

// 获取日期范围内的所有日期
const getDateColumns = (startDate: Dayjs, endDate: Dayjs): { date: string; key: string }[] => {
  const columns: { date: string; key: string }[] = [];
  let current = startDate.clone();
  while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
    columns.push({
      date: current.format(dateFormat),
      key: current.format('YYYY-MM-DD'),
    });
    current = current.add(1, 'day');
  }
  return columns;
};

const BillingMeterReport: React.FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [form] = Form.useForm();
  const pagingInfo = usePaging(10);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [updateState, updateFn] = useUpdate();

  // 默认统计时间：近31天（含当天）
  const defaultDateRange = useMemo<[Dayjs, Dayjs]>(() => [dayjs().subtract(30, 'day'), dayjs()], []);

  const [searchParams, setSearchParams] = useState<SearchParams>({
    startTime: defaultDateRange[0].startOf('date').valueOf(),
    endTime: defaultDateRange[1].endOf('date').valueOf(),
  });

  const { data: departmentList } = useRequest(
    async () => {
      const res = await apiV2BillingMeterReportDepartmentsPost({});
      return res?.list || [];
    },
    {
      refreshDeps: [updateState],
    }
  );

  const { data: deviceNameList } = useRequest(
    async () => {
      const res = await apiV2BillingMeterConfigListPost({});
      return res?.list || [];
    },
    {
      refreshDeps: [updateState],
    }
  );

  useEffect(() => {
    form.setFieldsValue({ dateRange: defaultDateRange });
  }, []);

  // 部门选项
  const departmentOptions = useMemo(() => {
    return (departmentList || []).map((item: string) => ({
      label: item,
      value: item,
    }));
  }, [departmentList]);

  // 设备名称选项
  const deviceNameOptions = useMemo(() => {
    return (deviceNameList || []).map((item: any) => ({
      label: item.deviceName,
      value: item.deviceName,
    }));
  }, [deviceNameList]);

  // 生成动态日期列
  const dateColumns = useMemo(() => {
    if (searchParams.startTime && searchParams.endTime) {
      return getDateColumns(dayjs(searchParams.startTime), dayjs(searchParams.endTime));
    }
    return getDateColumns(defaultDateRange[0], defaultDateRange[1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.startTime, searchParams.endTime]);

  const { data: tableData, loading: tableLoading } = useRequest(
    async () => {
      const res = await apiV2BillingMeterReportPagePost({
        ...searchParams,
        page: pageOffset,
        size: pageSize,
      });
      setTotalCount(res?.total || 0);

      // 转换数据格式：将 dailyDataList 扁平化到行数据中
      const list = res?.list || [];
      return list.map((item: BillingMeterReportItem) => {
        const rowData: TableRowData = {
          key: `${item.collectorCode}-${item.department}-${item.deviceName}`,
          collectorCode: item.collectorCode,
          department: item.department,
          deviceName: item.deviceName,
        };
        // 将 dailyDataList 中的数据按日期作为 key 扁平化
        (item.dailyDataList || []).forEach(daily => {
          if (daily.date) {
            rowData[daily.date] = daily.powerConsumption;
          }
        });
        return rowData;
      });
    },
    {
      refreshDeps: [pageOffset, pageSize, searchParams, updateState],
    }
  );

  // 表格列配置
  const columns = [
    {
      title: '采集器编号',
      dataIndex: 'collectorCode',
      ellipsis: true,
      width: 150,
      fixed: 'left' as const,
      render: (v: string) => <EllipsisSpan value={v ?? '-'} />,
    },
    {
      title: '部门',
      dataIndex: 'department',
      ellipsis: true,
      width: 120,
      fixed: 'left' as const,
      render: (v: string) => <EllipsisSpan value={v ?? '-'} />,
    },
    {
      title: '设备名称',
      dataIndex: 'deviceName',
      ellipsis: true,
      width: 150,
      fixed: 'left' as const,
      render: (v: string) => <EllipsisSpan value={v ?? '-'} />,
    },
    ...dateColumns.map(col => ({
      title: (
        <div>
          {col.date}
          <div>用电量(kWh)</div>
        </div>
      ),
      dataIndex: col.key,
      ellipsis: true,
      width: 140,
      render: (v: number) => (
        <EllipsisSpan value={v && <span style={{ color: 'var(--mx-diagram1-color)' }}>{keepTwoOrLine(v)}</span>} />
      ),
    })),
  ];

  // 导入前校验
  const beforeUpload = (file: File) => {
    const fileName = file.name.toLowerCase();
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

    if (!isExcel) {
      message.error('文件格式错误！仅支持 .xlsx、.xls');
      return AntdUpload.LIST_IGNORE;
    }
    return true;
  };

  // 导入状态变化
  const handleImportChange = (info: UploadChangeParam<UploadFile>) => {
    const { file } = info;
    if (file.status === 'done') {
      if (file.response?.code === 0) {
        message.success('导入成功');
        updateFn();
        return;
      }
      const errorMsg = file.response?.msg || '导入失败';
      message.error(errorMsg);
    } else if (file.status === 'error') {
      const errorMsg = file.response?.msg || '导入失败';
      message.error(errorMsg);
    }
  };

  // 下载已导入的配置
  const handleExportConfig = () => {
    window.open('/api/v2/billingMeter/config/export', '_blank');
  };

  // 下载报表数据
  const handleDownload = () => {
    const query = new URLSearchParams({
      startTime: String(searchParams.startTime),
      endTime: String(searchParams.endTime),
    });
    window.open(`/api/v2/billingMeter/report/export?${query.toString()}`);
  };

  // 筛选提交
  const onFinish = (values: FilterParams) => {
    setPageOffset(1);
    const params: SearchParams = {};

    if (values.department) params.department = values.department;
    if (values.collectorCode) params.collectorCode = values.collectorCode;
    if (values.deviceName) params.deviceName = values.deviceName;

    if (values.dateRange) {
      // 校验最大31天
      const diffDays = values.dateRange[1].diff(values.dateRange[0], 'day');
      if (diffDays > 30) {
        message.warning('统计时间范围不能超过31天');
        return;
      }
      params.startTime = values.dateRange[0].startOf('date').valueOf();
      params.endTime = values.dateRange[1].endOf('date').valueOf();
    } else {
      // 默认近31天
      params.startTime = defaultDateRange[0].startOf('date').valueOf();
      params.endTime = defaultDateRange[1].endOf('date').valueOf();
    }

    setSearchParams(params);
  };

  // 筛选重置
  const onReset = () => {
    setPageOffset(1);
    form.resetFields();
    setSearchParams({
      startTime: defaultDateRange[0].startOf('date').valueOf(),
      endTime: defaultDateRange[1].endOf('date').valueOf(),
    });
  };

  const filters = (
    <CustomFilter<FilterParams>
      form={form}
      initialValues={{ dateRange: defaultDateRange }}
      onFinish={onFinish}
      onReset={onReset}
    >
      <Form.Item name="department" label="部门">
        <Select
          showSearch
          placeholder="请选择"
          options={departmentOptions}
          optionFilterProp="label"
          allowClear
          style={{ width: '100%' }}
        />
      </Form.Item>
      <Form.Item name="collectorCode" label="采集器编号">
        <Input placeholder="请输入" allowClear />
      </Form.Item>
      <Form.Item name="deviceName" label="设备名称">
        <Select
          showSearch
          placeholder="请选择"
          options={deviceNameOptions}
          optionFilterProp="label"
          allowClear
          style={{ width: '100%' }}
        />
      </Form.Item>
      <Form.Item name="dateRange" label="统计时间">
        <DatePicker.RangePicker
          style={{ width: '100%' }}
          format={dateFormat}
          placeholder={['开始时间', '结束时间']}
          disabledDate={(current, { from }) => {
            if (!from) return false;
            return Math.abs(current.startOf('day').diff(from.startOf('day'), 'day')) > 30;
          }}
        />
      </Form.Item>
    </CustomFilter>
  );

  return (
    <Wrapper routes={breadcrumbRoutes?.routes} filters={filters} className={styles.wrapper}>
      <div className={styles.header}>
        <Space size="small">
          <AntdUpload
            action="/api/v2/billingMeter/config/import"
            accept=".xlsx,.xls"
            maxCount={1}
            showUploadList={false}
            beforeUpload={beforeUpload}
            onChange={handleImportChange}
          >
            <Button type="primary">导入计费电表配置</Button>
          </AntdUpload>
          <Button type="link" onClick={handleExportConfig}>
            下载已导入的配置
          </Button>
        </Space>
        <Button type="primary" onClick={handleDownload}>
          下载
        </Button>
      </div>
      <Table rowKey="key" sticky loading={tableLoading} columns={columns} dataSource={tableData} scroll={{ x: 1366 }} />
      <Paging pagingInfo={pagingInfo} />
    </Wrapper>
  );
};

export default BillingMeterReport;
